import React, { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useCurrentApplication from 'hooks/useCurrentApplication';
import { MainRoutes } from 'helpers/routes';

import SectionWrapper from 'components/layout/SectionWrapper';
import VerificationHeader from 'components/layout/VerificationHeader';
import VersionInfoModal from 'components/modals/VersionInfoModal';
import ArchiveModal from 'components/modals/ArchiveModal';
import DeleteModal from 'components/modals/DeleteModal';
import ApplicationPage from './ApplicationPage';

interface ContainerProps {
  children: ReactNode;
}

const Container: React.FC<ContainerProps> = ({ children }) => {
  const {
    appData,
    appDelete,
    appArchive,
    appUnarchive,
  } = useCurrentApplication();
  const navigate = useNavigate();
  const [showVersionInfo, setShowVersionInfo] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const isArchived = !!appData.archivedAt;

  const handleArchive = async (reason: string) => {
    setArchiving(true);
    if (isArchived) {
      await appUnarchive({
        reason,
        cb: () => setShowArchive(false),
      });
    } else {
      await appArchive({ reason, cb: () => setShowArchive(false) });
    }
    setArchiving(false);
  };

  const handleDelete = () => {
    appDelete(() => navigate(MainRoutes.VERIFICATION));
    setShowDelete(false);
  };

  const versions = {
    prqVersion: appData.prqVersion,
    meetingsAndRoundsVersion: appData.meetingsAndRoundsVersion,
    keyFindingsVersion: appData.keyFindingsVersion,
  };

  return (
    <SectionWrapper>
      <VerificationHeader
        burnCenterName={appData.bcqpBurnCenterName}
        applicationType={appData.applicationType}
        applicationPopulation={appData.applicationPopulation}
        state={appData.state}
        status={appData.status}
        requestedAt={appData.requestedAt}
        siteVisitAt={appData.siteVisitAt}
        expiredAt={appData.expiredAt}
        archivedAt={appData.archivedAt}
        isSample={appData.isSample || false}
        onVersionInfo={() => setShowVersionInfo(true)}
        onArchive={() => setShowArchive(true)}
        onUnarchive={() => setShowArchive(true)}
        onDelete={isArchived ? undefined : () => setShowDelete(true)}
      />
      <ApplicationPage />
      <div className="mt-4">{children}</div>

      <VersionInfoModal
        show={showVersionInfo}
        onHide={() => setShowVersionInfo(false)}
        versions={versions}
      />

      <ArchiveModal
        show={showArchive}
        onHide={() => setShowArchive(false)}
        onConfirm={handleArchive}
        verificationName={appData.bcqpBurnCenterName}
        mode={isArchived ? 'unarchive' : 'archive'}
        isSubmitting={archiving}
      />

      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={handleDelete}
      />
    </SectionWrapper>
  );
};

export default Container;
